import { formatDate } from '@/utils/format';
import type { TermType, TradeStatus } from '@/types/trades.types';

/**
 * The lifecycle scalars the note reads. Structural rather than a concrete trade
 * type: both dialogs that stamp a close date pass their own shape — the blotter
 * a `TradeItem`, the detail page a `PositionDetail`.
 */
export interface ClosureScheduleFields {
  status: TradeStatus;
  maturity_date?: string | null;
  // Read by neither branch since LR-695; kept so both callers still pass their
  // whole trade shape without a cast.
  term_type?: TermType;
  notice_served?: boolean;
}

/**
 * LR-610: on an `active` trade a close date is a plan, not a settlement.
 *
 * Closure runs only on `matured`, and the maturity cron is the only thing that
 * produces `matured` without an operator — so a date picked before the maturity
 * executes on the maturity date, not on the day picked here. The mirror of the
 * backend's `apps/positions/closure_schedule.py::effective_close_date`, said at
 * the moment the date is chosen: the write is accepted either way, so nothing
 * downstream would ever tell the trader when the date will really act.
 *
 * LR-695 retired the other half of the note — "no maturity date, so nothing
 * will close it". Set Close Date is no longer offered on an active trade without
 * a maturity date (see `getValidActions`), and Terminate writes the end date
 * together with the close date, so the dialog never opens on a trade the date
 * could be inert for.
 *
 * SOFT, like the holiday note — it never gates Confirm.
 *
 * Returns `null` when the picked date needs no explanation.
 */
export function getClosureScheduleNote(
  trade: ClosureScheduleFields | null | undefined,
  pickedDate: string,
): string | null {
  if (!trade || trade.status !== 'active' || !trade.maturity_date) return null;

  if (pickedDate && pickedDate < trade.maturity_date) {
    return `Closure runs only once the trade has matured, so it will settle on ${formatDate(trade.maturity_date)} — not earlier.`;
  }

  return null;
}
